/**
 * Admin — User Management
 *
 * Sortable, filterable user list with:
 *   - Live online status via SignalR presence store
 *   - Name search + country / status dropdown filters
 *   - Name, @screenName, operational community, join date, last login
 *   - Country, over-18 flag (placeholder)
 *   - Suspend access toggle with confirmation dialog
 *   - Expandable detail panel per user (permissions, features, audit)
 *   - IP address + IP-based location in detail view
 *
 * Reads from the same `apiSecurityUsers` data that App.tsx uses.
 * English only — system admin UI.
 */

import React, { useState, useMemo, useCallback } from 'react';
import CloseButton from '../../Components/CloseButton/CloseButton';
import { useSyncExternalStore } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { apiFetch } from '../../api/client';
import { mediaSrc } from '../../api/mediaUrl';
import {
  subscribe as presenceSubscribe,
  getSnapshot as presenceSnapshot,
  getUserPresence,
  getOnlineUsers,
} from '../../presence/presenceStore';
import AdminWaitlist from './AdminWaitlist';
import InviteUserModal from './InviteUserModal';
import SignInMethodsPanel from './SignInMethodsPanel';
import AppAccessPanel from './AppAccessPanel';
import AdminUserAccessModal from '../../Components/AdminUserAccessModal/AdminUserAccessModal';
import styles from './AdminUserManagement.module.css';

// ── Types ────────────────────────────────────────────────────

/** The seed-shape user record from useApiSecurityData */
export interface AdminUser {
  user_guid: string;
  name: string;
  display_name: string;
  email?: string;
  screen_name?: string;
  country_code?: string;
  operational_community_guid?: string;
  primary_location_entity_guid?: string;
  profile_image_url?: string;
  is_system_admin?: boolean;
  is_active?: boolean;
  created_at?: string;
  last_login_at?: string;
  last_login_ip?: string;
}

export interface AdminUserManagementProps {
  users: AdminUser[];
  communityEntities?: Array<{ entity_guid: string; name: string }>;
  /** GUID of the currently logged-in admin — self-suspension is blocked */
  currentAdminGuid?: string;
  /** Callback to persist suspension changes */
  onToggleSuspend?: (userGuid: string, suspend: boolean, reason: string) => void;
  /**
   * DEV-1470. Refetch the user list in place after a mutation. Replaces the
   * window.location.reload() the delete flow used to fire — no navigation, no
   * cold boot, no bounce through the auth refresh path.
   */
  onUsersChanged: () => void;
}

// ── Helpers ──────────────────────────────────────────────────

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function accountAge(iso?: string): string {
  if (!iso) return '—';
  try {
    const created = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days < 1) return 'Today';
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.floor(days / 30)}mo`;
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return months > 0 ? `${years}y ${months}mo` : `${years}y`;
  } catch {
    return '—';
  }
}

function countryFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  const offset = 0x1f1e6;
  return (
    String.fromCodePoint(code.charCodeAt(0) - 65 + offset) +
    String.fromCodePoint(code.charCodeAt(1) - 65 + offset)
  );
}

// ── Sort type ────────────────────────────────────────────────

type SortField = 'name' | 'screen_name' | 'community' | 'status' | 'country';
type SortDir = 'asc' | 'desc';

// ── Filter types ─────────────────────────────────────────────

type OnlineFilter = 'all' | 'online' | 'offline';
type StatusFilter = 'all' | 'active' | 'suspended';

// ── Suspend Confirmation Dialog ──────────────────────────────

function SuspendDialog({
  user,
  onConfirm,
  onCancel,
}: {
  user: AdminUser;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const isSuspending = user.is_active !== false;

  return (
    <div className={styles.dialogOverlay} onClick={onCancel}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.dialogTitle}>
          {isSuspending ? 'Suspend User Access' : 'Reactivate User Access'}
        </h3>
        <p className={styles.dialogText}>
          {isSuspending ? (
            <>
              This will <strong>immediately</strong> revoke access for{' '}
              <strong>{user.display_name}</strong>. Their active session will be
              invalidated on the next API call (typically within seconds). The user
              will see a &quot;Your account has been suspended&quot; message.
            </>
          ) : (
            <>
              This will restore access for <strong>{user.display_name}</strong>.
              They will be able to log in again immediately.
            </>
          )}
        </p>
        <label className={styles.dialogLabel}>
          {isSuspending ? 'Reason for suspension (required)' : 'Reactivation note (optional)'}
          <textarea
            className={styles.dialogTextarea}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isSuspending
                ? 'e.g. Policy violation, security concern, account review...'
                : 'e.g. Issue resolved, appeal approved...'
            }
            rows={3}
          />
        </label>
        <div className={styles.dialogActions}>
          <button className={styles.dialogBtnCancel} onClick={onCancel}>
            Cancel
          </button>
          <button
            className={
              isSuspending ? styles.dialogBtnDanger : styles.dialogBtnConfirm
            }
            onClick={() => onConfirm(reason)}
            disabled={isSuspending && !reason.trim()}
          >
            {isSuspending ? 'Suspend Access' : 'Reactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User Detail Panel ────────────────────────────────────────

function UserDetailPanel({
  user,
  communityName,
  onClose,
  onSuspendClick,
  onImpersonateClick,
  onDeleteClick,
  onManageAccessClick,
  isCurrentAdmin,
  presenceStatus,
}: {
  user: AdminUser;
  communityName: string;
  onClose: () => void;
  onSuspendClick: () => void;
  /** Start impersonating this user (DEV-555) */
  onImpersonateClick: () => void;
  /** Delete this user and wipe data (DEV-555) */
  onDeleteClick: () => void;
  /** Open the access / features override modal. */
  onManageAccessClick: () => void;
  /** True if this user is the currently logged-in admin */
  isCurrentAdmin: boolean;
  /** Live presence status from presence store */
  presenceStatus: 'online' | 'idle' | 'offline';
}) {
  const isSuspended = user.is_active === false;

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          {user.profile_image_url ? (
            <img
              src={mediaSrc(user.profile_image_url) ?? undefined}
              alt=""
              className={styles.detailAvatar}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div className={styles.detailAvatarPlaceholder}>
              {initials(user.display_name || user.name)}
            </div>
          )}
          <div>
            <h3 className={styles.detailName}>{user.display_name}</h3>
            {user.screen_name && (
              <span className={styles.detailScreen}>@{user.screen_name}</span>
            )}
            {user.email && (
              <span className={styles.detailEmail}>{user.email}</span>
            )}
          </div>
        </div>
        <CloseButton onClick={onClose} size="md" />
      </div>

      {/* ── Status badges ── */}
      <div className={styles.detailBadges}>
        <span
          className={`${styles.badge} ${isSuspended ? styles.badgeDanger : styles.badgeSuccess}`}
        >
          {isSuspended ? 'Suspended' : 'Active'}
        </span>
        {presenceStatus === 'online' && !isSuspended && (
          <span className={`${styles.badge} ${styles.badgeOnline}`}>Online Now</span>
        )}
        {presenceStatus === 'idle' && !isSuspended && (
          <span className={`${styles.badge} ${styles.badgeAway}`}>Away</span>
        )}
        {user.is_system_admin && (
          <span className={`${styles.badge} ${styles.badgeAdmin}`}>System Admin</span>
        )}
        {isCurrentAdmin && (
          <span className={`${styles.badge} ${styles.badgeSelf}`}>You</span>
        )}
      </div>

      {/* ── Info grid ── */}
      <div className={styles.detailGrid}>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>User GUID</span>
          <span className={styles.detailFieldValue} style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {user.user_guid}
          </span>
        </div>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>Community</span>
          <span className={styles.detailFieldValue}>{communityName}</span>
        </div>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>Country</span>
          <span className={styles.detailFieldValue}>
            {user.country_code
              ? `${countryFlag(user.country_code.toUpperCase())} ${user.country_code.toUpperCase()}`
              : '—'}
          </span>
        </div>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>Joined</span>
          <span className={styles.detailFieldValue}>{formatDate(user.created_at)}</span>
        </div>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>Last Login</span>
          <span className={styles.detailFieldValue}>{formatDateTime(user.last_login_at)}</span>
        </div>
        <div className={styles.detailField}>
          <span className={styles.detailFieldLabel}>Age Verified (18+)</span>
          <span className={styles.detailFieldValue} style={{ color: '#9ca3af', fontStyle: 'italic' }}>
            Not yet implemented
          </span>
        </div>
      </div>

      {/* ── Network / IP section ── */}
      <div className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Network &amp; Location</h4>
        <div className={styles.detailGrid}>
          <div className={styles.detailField}>
            <span className={styles.detailFieldLabel}>Last Login IP</span>
            <span className={styles.detailFieldValue} style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {user.last_login_ip || '—'}
            </span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailFieldLabel}>IP Location</span>
            <span className={styles.detailFieldValue}>
              {user.country_code
                ? `${countryFlag(user.country_code.toUpperCase())} ${user.country_code.toUpperCase()}`
                : '—'}
              <span style={{ display: 'block', fontSize: 10, color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>
                GeoIP lookup not yet wired
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* ── Permissions (placeholder sections) ── */}
      <div className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Permissions &amp; Roles</h4>
        <p className={styles.detailPlaceholder}>
          Community roles, project ACLs, and collection memberships will appear here.
        </p>
      </div>

      <div className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Feature Overrides</h4>
        <p className={styles.detailPlaceholder}>
          Per-user feature flag overrides will appear here.
        </p>
      </div>

      <div className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Activity &amp; Audit</h4>
        <p className={styles.detailPlaceholder}>
          Login history, content actions, and admin actions will appear here.
        </p>
      </div>

      {/* ── Admin Actions (DEV-555) ── */}
      {/* ── Access & Features ── */}
      <div className={styles.detailSection}>
        <h4 className={styles.detailSectionTitle}>Access &amp; Features</h4>
        <div className={styles.dangerRow}>
          <div>
            <span className={styles.dangerLabel}>Manage access</span>
            <span className={styles.dangerHint}>
              View and manually override what this user can see and do.
            </span>
          </div>
          <button className={styles.btnReactivate} onClick={onManageAccessClick}>
            Manage
          </button>
        </div>
      </div>

      {!isCurrentAdmin && (
        <div className={styles.detailSection}>
          <h4 className={styles.detailSectionTitle}>Admin Actions</h4>
          <div className={styles.dangerRow}>
            <div>
              <span className={styles.dangerLabel}>View as User</span>
              <span className={styles.dangerHint}>
                Impersonate this user to see the platform from their perspective.
              </span>
            </div>
            <button className={styles.btnReactivate} onClick={onImpersonateClick}>
              Impersonate
            </button>
          </div>
        </div>
      )}

      {/* ── Suspend / Reactivate ── */}
      <div className={styles.detailDanger}>
        {isCurrentAdmin ? (
          <div className={styles.dangerRow}>
            <div>
              <span className={styles.dangerLabel}>Self-Suspension Blocked</span>
              <span className={styles.dangerHint}>
                You cannot suspend your own account. Another system admin must perform this action.
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.dangerRow}>
              <div>
                <span className={styles.dangerLabel}>
                  {isSuspended ? 'Account Suspended' : 'Suspend Access'}
                </span>
                <span className={styles.dangerHint}>
                  {isSuspended
                    ? 'User is currently locked out. Reactivate to restore access.'
                    : 'Immediately revoke this user\'s access to the platform.'}
                </span>
              </div>
              <button
                className={isSuspended ? styles.btnReactivate : styles.btnSuspend}
                onClick={onSuspendClick}
              >
                {isSuspended ? 'Reactivate' : 'Suspend'}
              </button>
            </div>
            <div className={styles.dangerRow}>
              <div>
                <span className={styles.dangerLabel}>Delete User &amp; Wipe Data</span>
                <span className={styles.dangerHint}>
                  Permanently delete this account and all associated data. The email can re-register afterward.
                </span>
              </div>
              <button className={styles.btnSuspend} onClick={onDeleteClick}>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────

export default function AdminUserManagement({
  users,
  communityEntities = [],
  currentAdminGuid,
  onUsersChanged,
  onToggleSuspend,
}: AdminUserManagementProps) {
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<AdminUser | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'waitlist'>('users');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [waitlistRefreshKey, setWaitlistRefreshKey] = useState(0);

  // ── Impersonation & Delete (DEV-555) ──
  const { impersonate } = useAuth();
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [accessTarget, setAccessTarget] = useState<AdminUser | null>(null);

  const handleImpersonate = useCallback(async (userGuid: string) => {
    try {
      await impersonate(userGuid);
      // Redirect to home so we see the impersonated user's view
      window.location.href = '/';
    } catch (err: any) {
      alert(err.message || 'Impersonation failed');
    }
  }, [impersonate]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/api/admin/users/${deleteTarget.user_guid}`, { method: 'DELETE' });
      setDeleteTarget(null);
      setSelectedGuid(null);
      // DEV-1470: refetch the list IN PLACE. The old code did
      // window.location.reload() — a full cold boot of the app just to drop one
      // row from a table. That cost more than a wasted render: the reload tripped
      // a silent token refresh, and refresh used to re-challenge admins for TOTP
      // (no trusted devices), so deleting a user could bounce the admin to the 2FA
      // screen. DEV-1469 fixes the auth half of that; this removes the reason it
      // ever came up.
      onUsersChanged();
    } catch (err: any) {
      alert(err.message || 'Delete failed');
    }
  }, [deleteTarget, onUsersChanged]);

  // ── Live presence ──
  // Subscribe to the presence store so the component re-renders when online statuses change
  const _presenceVersion = useSyncExternalStore(presenceSubscribe, presenceSnapshot, presenceSnapshot);

  /** Get presence status for a user: 'online' | 'idle' | 'offline' */
  const getPresenceStatus = useCallback(
    (userGuid: string) => getUserPresence(userGuid),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_presenceVersion],
  );

  const onlineUserGuids = useMemo(() => {
    // Re-compute when presence changes
    void _presenceVersion;
    return new Set(getOnlineUsers());
  }, [_presenceVersion]);

  /** Returns true if the given user is the currently logged-in admin */
  const isSelf = useCallback(
    (userGuid: string) =>
      !!currentAdminGuid &&
      userGuid.toUpperCase() === currentAdminGuid.toUpperCase(),
    [currentAdminGuid],
  );

  // ── Community lookup ──
  const communityMap = useMemo(() => {
    const map = new Map<string, string>();
    communityEntities.forEach((e) =>
      map.set(e.entity_guid.toUpperCase(), e.name),
    );
    return map;
  }, [communityEntities]);

  const getCommunityName = useCallback(
    (guid?: string) => {
      if (!guid) return '—';
      return communityMap.get(guid.toUpperCase()) || guid.substring(0, 8) + '...';
    },
    [communityMap],
  );

  // ── Build distinct country list for the dropdown ──
  const countryOptions = useMemo(() => {
    const codes = new Set<string>();
    users.forEach((u) => {
      if (u.country_code) codes.add(u.country_code.toUpperCase());
    });
    return Array.from(codes).sort();
  }, [users]);

  // ── Filter + sort ──
  const filtered = useMemo(() => {
    let list = users;

    // Text search
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (u) =>
          u.display_name?.toLowerCase().includes(q) ||
          u.name?.toLowerCase().includes(q) ||
          u.screen_name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.user_guid.toLowerCase().includes(q),
      );
    }

    // Country filter
    if (countryFilter !== 'all') {
      list = list.filter(
        (u) => u.country_code?.toUpperCase() === countryFilter,
      );
    }

    // Online filter
    if (onlineFilter === 'online') {
      list = list.filter((u) => onlineUserGuids.has(u.user_guid.toUpperCase()));
    } else if (onlineFilter === 'offline') {
      list = list.filter((u) => !onlineUserGuids.has(u.user_guid.toUpperCase()));
    }

    // Account status filter
    if (statusFilter === 'active') {
      list = list.filter((u) => u.is_active !== false);
    } else if (statusFilter === 'suspended') {
      list = list.filter((u) => u.is_active === false);
    }

    const compare = (a: AdminUser, b: AdminUser): number => {
      let av = '';
      let bv = '';
      switch (sortField) {
        case 'name':
          av = a.display_name || a.name || '';
          bv = b.display_name || b.name || '';
          break;
        case 'screen_name':
          av = a.screen_name || '';
          bv = b.screen_name || '';
          break;
        case 'community':
          av = getCommunityName(a.operational_community_guid);
          bv = getCommunityName(b.operational_community_guid);
          break;
        case 'status':
          av = a.is_active === false ? 'suspended' : 'active';
          bv = b.is_active === false ? 'suspended' : 'active';
          break;
        case 'country':
          av = a.country_code || '';
          bv = b.country_code || '';
          break;
      }
      const cmp = av.localeCompare(bv, 'en', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    };

    return [...list].sort(compare);
  }, [users, search, countryFilter, onlineFilter, statusFilter, sortField, sortDir, getCommunityName, onlineUserGuids]);

  const selectedUser = useMemo(
    () =>
      selectedGuid
        ? users.find(
            (u) => u.user_guid.toUpperCase() === selectedGuid.toUpperCase(),
          ) || null
        : null,
    [users, selectedGuid],
  );

  // ── Counts for filter labels ──
  const onlineCount = useMemo(() => {
    return users.filter((u) => onlineUserGuids.has(u.user_guid.toUpperCase())).length;
  }, [users, onlineUserGuids]);

  const hasActiveFilters = countryFilter !== 'all' || onlineFilter !== 'all' || statusFilter !== 'all';

  // ── Sort handler ──
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortArrow = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  const clearFilters = () => {
    setCountryFilter('all');
    setOnlineFilter('all');
    setStatusFilter('all');
    setSearch('');
  };

  // ── Suspend handler ──
  const handleSuspendConfirm = (reason: string) => {
    if (!suspendTarget) return;
    const suspend = suspendTarget.is_active !== false;
    onToggleSuspend?.(suspendTarget.user_guid, suspend, reason);
    setSuspendTarget(null);
  };

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>User Management</h1>
          <p className={styles.subtitle}>
            {users.length} registered user{users.length !== 1 ? 's' : ''}
            {' \u00B7 '}{onlineCount} online
            {filtered.length !== users.length && ` \u00B7 ${filtered.length} shown`}
          </p>
        </div>
        <div className={styles.headerControls}>
          <button
            className={styles.inviteBtn}
            onClick={() => setShowInviteModal(true)}
          >
            + Invite User
          </button>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by name, email, GUID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Sign-in Methods (auth toggles) ── */}
      <SignInMethodsPanel />
      <AppAccessPanel />

      {/* ── Tabs: Users / Waitlist ── */}
      <div className={styles.pageTabs}>
        <button
          className={`${styles.pageTab} ${activeTab === 'users' ? styles.pageTabActive : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`${styles.pageTab} ${activeTab === 'waitlist' ? styles.pageTabActive : ''}`}
          onClick={() => setActiveTab('waitlist')}
        >
          Waitlist
        </button>
      </div>

      {/* ── Waitlist Tab ── */}
      {activeTab === 'waitlist' && (
        <AdminWaitlist key={waitlistRefreshKey} />
      )}

      {/* ── Users Tab: Filter bar ── */}
      {activeTab === 'users' && <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={onlineFilter}
          onChange={(e) => setOnlineFilter(e.target.value as OnlineFilter)}
        >
          <option value="all">All statuses</option>
          <option value="online">Online ({onlineCount})</option>
          <option value="offline">Offline</option>
        </select>

        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">All accounts</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>

        <select
          className={styles.filterSelect}
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
        >
          <option value="all">All countries</option>
          {countryOptions.map((code) => (
            <option key={code} value={code}>
              {countryFlag(code)} {code}
            </option>
          ))}
        </select>

        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>}

      {activeTab === 'users' && <div className={styles.layout}>
        {/* ── User Table ── */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thNarrow}>Status</th>
                <th
                  className={styles.thSortable}
                  onClick={() => handleSort('name')}
                >
                  User{sortArrow('name')}
                </th>
                <th
                  className={styles.thSortable}
                  onClick={() => handleSort('screen_name')}
                >
                  Screen Name{sortArrow('screen_name')}
                </th>
                <th
                  className={styles.thSortable}
                  onClick={() => handleSort('community')}
                >
                  Community{sortArrow('community')}
                </th>
                <th>Joined</th>
                <th>Last Login</th>
                <th
                  className={styles.thSortable}
                  onClick={() => handleSort('country')}
                >
                  Country{sortArrow('country')}
                </th>
                <th>18+</th>
                <th
                  className={styles.thSortable}
                  onClick={() => handleSort('status')}
                >
                  Access{sortArrow('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSuspended = u.is_active === false;
                const isSelected =
                  selectedGuid?.toUpperCase() === u.user_guid.toUpperCase();
                const presenceStatus = getPresenceStatus(u.user_guid);

                return (
                  <tr
                    key={u.user_guid}
                    className={`${styles.row} ${isSelected ? styles.rowSelected : ''} ${isSuspended ? styles.rowSuspended : ''}`}
                    onClick={() =>
                      setSelectedGuid(
                        isSelected ? null : u.user_guid,
                      )
                    }
                  >
                    {/* Online status */}
                    <td className={styles.tdCenter}>
                      <span
                        className={styles.onlineDot}
                        title={
                          isSuspended
                            ? 'Suspended'
                            : presenceStatus === 'online'
                              ? 'Online'
                              : presenceStatus === 'idle'
                                ? 'Away'
                                : 'Offline'
                        }
                        style={{
                          background: isSuspended
                            ? '#ef4444'
                            : presenceStatus === 'online'
                              ? '#22c55e'
                              : presenceStatus === 'idle'
                                ? '#f59e0b'
                                : '#d1d5db',
                        }}
                      />
                    </td>

                    {/* User */}
                    <td>
                      <div className={styles.userCell}>
                        {u.profile_image_url ? (
                          <img
                            src={mediaSrc(u.profile_image_url) ?? undefined}
                            alt=""
                            className={styles.avatar}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className={styles.avatarPlaceholder}>
                            {initials(u.display_name || u.name)}
                          </div>
                        )}
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>
                            {u.display_name || u.name}
                          </span>
                          {u.is_system_admin && (
                            <span className={styles.adminTag}>Admin</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Screen name */}
                    <td className={styles.tdMono}>
                      {u.screen_name ? `@${u.screen_name}` : '—'}
                    </td>

                    {/* Community */}
                    <td>{getCommunityName(u.operational_community_guid)}</td>

                    {/* Joined */}
                    <td className={styles.tdSmall}>
                      {u.created_at ? (
                        <span title={formatDate(u.created_at)}>
                          {accountAge(u.created_at)}
                        </span>
                      ) : (
                        <span className={styles.tdPlaceholder}>—</span>
                      )}
                    </td>

                    {/* Last login */}
                    <td className={styles.tdSmall}>
                      {formatDateTime(u.last_login_at)}
                    </td>

                    {/* Country */}
                    <td className={styles.tdCenter}>
                      {u.country_code ? (
                        <span title={u.country_code.toUpperCase()}>
                          {countryFlag(u.country_code.toUpperCase())}{' '}
                          {u.country_code.toUpperCase()}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>

                    {/* 18+ */}
                    <td className={styles.tdCenter}>
                      <span className={styles.tdPlaceholder}>—</span>
                    </td>

                    {/* Access toggle */}
                    <td
                      className={styles.tdCenter}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isSelf(u.user_guid) ? (
                        <span className={styles.selfLabel} title="You cannot suspend your own account">
                          You
                        </span>
                      ) : (
                        <button
                          className={`${styles.accessToggle} ${isSuspended ? styles.accessSuspended : styles.accessActive}`}
                          onClick={() => setSuspendTarget(u)}
                          title={isSuspended ? 'Click to reactivate' : 'Click to suspend'}
                        >
                          <span className={styles.toggleTrack}>
                            <span className={styles.toggleKnob} />
                          </span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className={styles.emptyRow}>
                    {search || hasActiveFilters
                      ? 'No users match the current filters'
                      : 'No users found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Detail Panel ── */}
        {selectedUser && (
          <UserDetailPanel
            user={selectedUser}
            communityName={getCommunityName(selectedUser.operational_community_guid)}
            onClose={() => setSelectedGuid(null)}
            onSuspendClick={() => setSuspendTarget(selectedUser)}
            onImpersonateClick={() => handleImpersonate(selectedUser.user_guid)}
            onDeleteClick={() => setDeleteTarget(selectedUser)}
            onManageAccessClick={() => setAccessTarget(selectedUser)}
            isCurrentAdmin={isSelf(selectedUser.user_guid)}
            presenceStatus={getPresenceStatus(selectedUser.user_guid)}
          />
        )}

        {accessTarget && (
          <AdminUserAccessModal
            userGuid={accessTarget.user_guid}
            displayName={accessTarget.display_name}
            screenName={accessTarget.screen_name}
            onClose={() => setAccessTarget(null)}
          />
        )}
      </div>}

      {/* ── Suspend Confirmation ── */}
      {suspendTarget && (
        <SuspendDialog
          user={suspendTarget}
          onConfirm={handleSuspendConfirm}
          onCancel={() => setSuspendTarget(null)}
        />
      )}

      {/* ── Delete Confirmation (DEV-555) ── */}
      {deleteTarget && (
        <div className={styles.dialogOverlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.dialogTitle}>Delete User &amp; Wipe Data</h3>
            <p className={styles.dialogText}>
              Permanently delete <strong>{deleteTarget.display_name || deleteTarget.email}</strong> ({deleteTarget.email})
              and all associated data? This cannot be undone. The email will be able to re-register as a new user.
            </p>
            <div className={styles.dialogActions}>
              <button className={styles.dialogBtnCancel} onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className={styles.dialogBtnDanger} onClick={handleDeleteConfirm}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite User Modal (DEV-144) ── */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvited={() => setWaitlistRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
